<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'

import AppIcon from '../components/AppIcon.vue'
import PageBanner from '../components/PageBanner.vue'
import SiteFooter from '../components/SiteFooter.vue'
import SiteHeader from '../components/SiteHeader.vue'
import { ApiError, apiRequest, apiUpload } from '../services/api'
import { GAME_TRANSFER_FORMAT, GAME_TRANSFER_VERSION, type GameImportResult, type GameTransferDocument } from '../../shared/contracts/game-transfer'
import { formatStandardAnswers, parseStandardAnswers, type StandardAnswer } from '../../shared/domain/standard-answers'

interface Admin { id: string; email: string; name: string }
interface GameSummary { id: string; name: string; description: string | null; archived: boolean }
interface SessionSummary { id: string; join_code: string; status: string; phase: string; started_at: string; ended_at: string | null; game_name: string; player_count: number; team_count: number; winner: string | null }
type QuestionType = 'OPEN_TEXT' | 'MULTIPLE_CHOICE_TEXT' | 'MULTIPLE_CHOICE_IMAGE' | 'NUMERIC_MEDIAN' | 'NUMERIC_MODE'
interface Scoring { rank1?: number; rank2?: number; rank3?: number; other?: number; closest?: number; secondClosest?: number; remaining?: number; winning?: number }
interface Question { id: string; roundId: string; type: QuestionType; prompt: string; imageUrl: string | null; points: number; scoring: Scoring; choices: Array<{ id: string; label?: string; imageUrl?: string }> | null; timeLimitSeconds: number | null; hostNotes: string | null; active: boolean; standardAnswers?: StandardAnswer[] }
interface Round { id: string; name: string; sortOrder: number; questions: Question[] }
interface Theme { id: string; name: string; system: boolean; configuration: { background: string; primary: string; secondary: string; accent: string; text: string; cardBackground: string; buttonStyle: 'rounded' | 'square' | 'pill' }; logoUrl: string | null; backgroundUrl: string | null }
interface GameDetail extends GameSummary {
  themeId: string | null
  settings: { profanityFilterEnabled: boolean; allowNegativeTotalScore: boolean; answerAveragingEnabled?: boolean; maxTeamSize: number | null }
  rounds: Round[]
}

const typeLabels: Record<QuestionType, string> = {
  OPEN_TEXT: 'Open Text', MULTIPLE_CHOICE_TEXT: 'Multiple Choice Text', MULTIPLE_CHOICE_IMAGE: 'Multiple Choice Image',
  NUMERIC_MEDIAN: 'Numeric Median', NUMERIC_MODE: 'Numeric Mode',
}

const admin = ref<Admin | null>(null)
const games = ref<GameSummary[]>([])
const sessions = ref<SessionSummary[]>([])
const themes = ref<Theme[]>([])
const themeDraft = ref<Theme | null>(null)
const selected = ref<GameDetail | null>(null)
const loading = ref(true)
const busy = ref(false)
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const error = ref('')
const newGameName = ref('')
const newRoundName = ref('')
const newPrompt = ref('')
const questionRoundId = ref('')
const questionPoints = ref(100)
const questionTimeLimit = ref<number | null>(null)
const rank1 = ref(100)
const rank2 = ref(50)
const rank3 = ref(0)
const other = ref(-25)
const questionType = ref<QuestionType>('OPEN_TEXT')
const questionImageUrl = ref<string | null>(null)
const newStandardAnswers = ref('')
const standardAnswerText = ref<Record<string, string>>({})
const choiceLines = ref('')
const closest = ref(100)
const secondClosest = ref(50)
const remaining = ref(0)
const modeWinning = ref(100)
const importInput = ref<HTMLInputElement | null>(null)
const importDocument = ref<GameTransferDocument | null>(null)
const importFileName = ref('')
const importError = ref('')
const importMode = ref<'create' | 'append'>('create')
const importWarnings = ref<string[]>([])
const router = useRouter()

const activeGames = computed(() => games.value.filter((game) => !game.archived).length)
const totalQuestions = computed(() => selected.value?.rounds.reduce((total, round) => total + round.questions.length, 0) ?? 0)

const importSummary = computed(() => {
  const game = importDocument.value?.game
  if (!game) return ''
  const questions = game.rounds.reduce((total, round) => total + (round.questions?.length ?? 0), 0)
  return `${game.name ?? importFileName.value} · ${game.rounds.length} ${game.rounds.length === 1 ? 'round' : 'rounds'} · ${questions} ${questions === 1 ? 'question' : 'questions'}`
})

function kind(question: { type?: QuestionType }): QuestionType {
  return question.type ?? 'OPEN_TEXT'
}

function exportUrl(gameId: string, roundId?: string): string {
  return roundId ? `/api/admin/games/${gameId}/rounds/${roundId}/export` : `/api/admin/games/${gameId}/export`
}

async function chooseImportFile(event: Event): Promise<void> {
  const file = (event.target as HTMLInputElement).files?.[0]
  importDocument.value = null
  importError.value = ''
  importWarnings.value = []
  if (!file) return
  importFileName.value = file.name
  let parsed: Partial<GameTransferDocument> | null = null
  try { parsed = JSON.parse(await file.text()) as Partial<GameTransferDocument> } catch { importError.value = 'That file is not valid JSON.'; return }
  if (parsed?.format !== GAME_TRANSFER_FORMAT || parsed.formatVersion !== GAME_TRANSFER_VERSION || !Array.isArray(parsed.game?.rounds)) {
    importError.value = 'That file is not a Crowd Control game export.'
    return
  }
  importDocument.value = parsed as GameTransferDocument
  if (!selected.value || selected.value.archived) importMode.value = 'create'
}

async function runImport(): Promise<void> {
  if (!importDocument.value) return
  const target = importMode.value === 'append' && selected.value && !selected.value.archived ? selected.value.id : null
  await run(async () => {
    const result = await apiRequest<GameImportResult>(target ? `/api/admin/games/${target}/import` : '/api/admin/games/import', {
      method: 'POST', body: JSON.stringify(importDocument.value),
    })
    importWarnings.value = result.warnings
    importDocument.value = null
    importFileName.value = ''
    if (importInput.value) importInput.value.value = ''
    await loadGames()
    await openGame(result.id)
  })
}

async function loadGames(): Promise<void> {
  games.value = (await apiRequest<{ games: GameSummary[] }>('/api/admin/games')).games
}

async function loadSessions(): Promise<void> {
  sessions.value = (await apiRequest<{ sessions: SessionSummary[] }>('/api/admin/session-history')).sessions
}

async function loadThemes(): Promise<void> {
  themes.value = (await apiRequest<{ themes: Theme[] }>('/api/admin/themes')).themes
}

function easternDate(value: string): string {
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/New_York' })
    .format(new Date(value))
}

async function openGame(id: string): Promise<void> {
  const result = await apiRequest<{ game: GameDetail }>(`/api/admin/games/${id}`)
  result.game.settings.answerAveragingEnabled ??= true
  selected.value = result.game
  questionRoundId.value ||= result.game.rounds[0]?.id ?? ''
  standardAnswerText.value = Object.fromEntries(result.game.rounds.flatMap((round) =>
    round.questions.map((question) => [question.id, formatStandardAnswers(question.standardAnswers ?? [])])))
}

onMounted(async () => {
  try {
    admin.value = (await apiRequest<{ admin: Admin }>('/api/auth/session')).admin
    await loadGames()
    await loadSessions()
    await loadThemes()
  } catch { admin.value = null } finally { loading.value = false }
})

async function run(action: () => Promise<void>): Promise<void> {
  error.value = ''
  busy.value = true
  try { await action() } catch (caught) {
    error.value = caught instanceof ApiError ? caught.message : 'The request could not be completed.'
  } finally { busy.value = false }
}

async function signIn(): Promise<void> {
  await run(async () => {
    admin.value = (await apiRequest<{ admin: Admin }>('/api/auth/login', {
      method: 'POST', body: JSON.stringify({ email: email.value, password: password.value }),
    })).admin
    password.value = ''
    await loadGames()
    await loadSessions()
    await loadThemes()
  })
}

async function signOut(): Promise<void> {
  await run(async () => {
    await apiRequest('/api/auth/logout', { method: 'POST', body: '{}' })
    admin.value = null
    selected.value = null
  })
}

async function createGame(): Promise<void> {
  await run(async () => {
    const result = await apiRequest<{ id: string }>('/api/admin/games', {
      method: 'POST', body: JSON.stringify({ name: newGameName.value }),
    })
    newGameName.value = ''
    await loadGames()
    await openGame(result.id)
  })
}

async function addRound(): Promise<void> {
  if (!selected.value) return
  await run(async () => {
    await apiRequest(`/api/admin/games/${selected.value!.id}/rounds`, {
      method: 'POST', body: JSON.stringify({ name: newRoundName.value }),
    })
    newRoundName.value = ''
    await openGame(selected.value!.id)
  })
}

async function addQuestion(): Promise<void> {
  if (!selected.value) return
  await run(async () => {
    const choices = questionType.value.startsWith('MULTIPLE_CHOICE')
      ? choiceLines.value.split('\n').map((value) => value.trim()).filter(Boolean).map((value, index) => ({
        id: `choice-${index + 1}`,
        ...(questionType.value === 'MULTIPLE_CHOICE_IMAGE' ? { imageUrl: value } : { label: value }),
      }))
      : undefined
    const scoring = questionType.value === 'NUMERIC_MEDIAN'
      ? { closest: closest.value, secondClosest: secondClosest.value, remaining: remaining.value }
      : questionType.value === 'NUMERIC_MODE'
        ? { winning: modeWinning.value, other: other.value }
        : { rank1: rank1.value, rank2: rank2.value, rank3: rank3.value, other: other.value }
    await apiRequest(`/api/admin/games/${selected.value!.id}/questions`, {
      method: 'POST',
      body: JSON.stringify({
        roundId: questionRoundId.value,
        type: questionType.value,
        prompt: newPrompt.value,
        imageUrl: questionImageUrl.value,
        points: questionPoints.value,
        timeLimitSeconds: questionTimeLimit.value,
        scoring,
        choices,
        standardAnswers: parseStandardAnswers(newStandardAnswers.value),
      }),
    })
    newPrompt.value = ''
    newStandardAnswers.value = ''
    questionImageUrl.value = null
    choiceLines.value = ''
    await openGame(selected.value!.id)
  })
}

async function upload(file: File, kind: 'question' | 'choice' | 'theme-logo' | 'theme-background'): Promise<string> {
  const form = new FormData()
  form.set('file', file)
  form.set('kind', kind)
  return (await apiUpload<{ url: string }>('/api/uploads/image', form)).url
}

function newTheme(): void {
  themeDraft.value = {
    id: '', name: 'New Theme', system: false,
    configuration: { background: '#1d1e24', primary: '#9cecfe', secondary: '#2a2d33', accent: '#ffba23', text: '#ffffff', cardBackground: '#2a2d33', buttonStyle: 'pill' },
    logoUrl: null, backgroundUrl: null,
  }
}

function editTheme(theme: Theme): void {
  themeDraft.value = structuredClone(theme)
}

async function saveTheme(): Promise<void> {
  if (!themeDraft.value) return
  await run(async () => {
    const path = themeDraft.value!.id ? `/api/admin/themes/${themeDraft.value!.id}` : '/api/admin/themes'
    const { name, configuration, logoUrl, backgroundUrl } = themeDraft.value!
    await apiRequest(path, {
      method: themeDraft.value!.id ? 'PUT' : 'POST', body: JSON.stringify({ name, configuration, logoUrl, backgroundUrl }),
    })
    await loadThemes()
    themeDraft.value = null
  })
}

async function uploadThemeImage(event: Event, field: 'logoUrl' | 'backgroundUrl'): Promise<void> {
  const input = event.currentTarget as HTMLInputElement
  const file = input.files?.[0]
  if (!file || !themeDraft.value) return
  await run(async () => { themeDraft.value![field] = await upload(file, field === 'logoUrl' ? 'theme-logo' : 'theme-background') })
  input.value = ''
}

async function uploadNewQuestionImage(event: Event): Promise<void> {
  const input = event.currentTarget as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  await run(async () => { questionImageUrl.value = await upload(file, 'question') })
  input.value = ''
}

async function uploadChoiceImages(event: Event): Promise<void> {
  const input = event.currentTarget as HTMLInputElement
  const files = [...(input.files ?? [])]
  if (!files.length) return
  await run(async () => {
    const urls = await Promise.all(files.map((file) => upload(file, 'choice')))
    choiceLines.value = [choiceLines.value, ...urls].filter(Boolean).join('\n')
  })
  input.value = ''
}

async function uploadQuestionImage(question: Question, event: Event): Promise<void> {
  const input = event.currentTarget as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  await run(async () => { question.imageUrl = await upload(file, 'question') })
  input.value = ''
}

async function duplicateGame(game: GameSummary): Promise<void> {
  await run(async () => {
    const result = await apiRequest<{ id: string }>(`/api/admin/games/${game.id}/duplicate`, { method: 'POST', body: '{}' })
    await loadGames()
    await openGame(result.id)
  })
}

async function archiveGame(game: GameSummary): Promise<void> {
  await run(async () => {
    await apiRequest(`/api/admin/games/${game.id}/archive`, { method: 'POST', body: '{}' })
    if (selected.value?.id === game.id) selected.value = null
    await loadGames()
  })
}

async function hostGame(): Promise<void> {
  if (!selected.value) return
  await run(async () => {
    const session = await apiRequest<{ sessionId: string }>(`/api/admin/games/${selected.value!.id}/sessions`, {
      method: 'POST', body: '{}',
    })
    await router.push(`/host/${session.sessionId}`)
  })
}

async function refreshSelected(): Promise<void> {
  if (selected.value) await openGame(selected.value.id)
}

async function saveGame(): Promise<void> {
  if (!selected.value) return
  await run(async () => {
    await apiRequest(`/api/admin/games/${selected.value!.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        name: selected.value!.name,
        description: selected.value!.description,
        themeId: selected.value!.themeId ?? 'system-default',
        profanityFilterEnabled: selected.value!.settings.profanityFilterEnabled,
        allowNegativeTotalScore: selected.value!.settings.allowNegativeTotalScore,
        answerAveragingEnabled: selected.value!.settings.answerAveragingEnabled ?? true,
        maxTeamSize: selected.value!.settings.maxTeamSize,
      }),
    })
    await loadGames()
    await refreshSelected()
  })
}

async function saveRound(round: Round): Promise<void> {
  await run(async () => {
    await apiRequest(`/api/admin/games/${selected.value!.id}/rounds/${round.id}`, {
      method: 'PUT', body: JSON.stringify({ name: round.name }),
    })
    await refreshSelected()
  })
}

async function deleteRound(round: Round): Promise<void> {
  if (!confirm(`Delete ${round.name} and all of its questions?`)) return
  await run(async () => {
    await apiRequest(`/api/admin/games/${selected.value!.id}/rounds/${round.id}`, { method: 'DELETE' })
    await refreshSelected()
  })
}

async function moveRound(index: number, direction: -1 | 1): Promise<void> {
  if (!selected.value) return
  const target = index + direction
  if (target < 0 || target >= selected.value.rounds.length) return
  const ordered = selected.value.rounds.map((round) => round.id)
  ;[ordered[index], ordered[target]] = [ordered[target]!, ordered[index]!]
  await run(async () => {
    await apiRequest(`/api/admin/games/${selected.value!.id}/rounds/reorder`, {
      method: 'POST', body: JSON.stringify({ orderedIds: ordered }),
    })
    await refreshSelected()
  })
}

async function saveQuestion(question: Question): Promise<void> {
  await run(async () => {
    await apiRequest(`/api/admin/games/${selected.value!.id}/questions/${question.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        roundId: question.roundId, type: question.type, prompt: question.prompt, imageUrl: question.imageUrl,
        choices: question.choices, points: question.points, scoring: question.scoring, timeLimitSeconds: question.timeLimitSeconds,
        hostNotes: question.hostNotes, active: question.active,
        standardAnswers: parseStandardAnswers(standardAnswerText.value[question.id] ?? ''),
      }),
    })
    await refreshSelected()
  })
}

async function duplicateQuestion(question: Question): Promise<void> {
  await run(async () => {
    await apiRequest(`/api/admin/games/${selected.value!.id}/questions/${question.id}/duplicate`, { method: 'POST', body: '{}' })
    await refreshSelected()
  })
}

async function deleteQuestion(question: Question): Promise<void> {
  if (!confirm('Delete this question?')) return
  await run(async () => {
    await apiRequest(`/api/admin/games/${selected.value!.id}/questions/${question.id}`, { method: 'DELETE' })
    await refreshSelected()
  })
}

async function moveQuestion(round: Round, index: number, direction: -1 | 1): Promise<void> {
  const target = index + direction
  if (target < 0 || target >= round.questions.length) return
  const ordered = round.questions.map((question) => question.id)
  ;[ordered[index], ordered[target]] = [ordered[target]!, ordered[index]!]
  await run(async () => {
    await apiRequest(`/api/admin/games/${selected.value!.id}/questions/reorder`, {
      method: 'POST', body: JSON.stringify({ roundId: round.id, orderedIds: ordered }),
    })
    await refreshSelected()
  })
}
</script>

<template>
  <div class="page-wrapper">
    <!-- session check -->
    <template v-if="loading">
      <SiteHeader minimal />
      <main class="section section--top">
        <div class="container container--narrow text-center">
          <p aria-live="polite">
            Checking your session…
          </p>
        </div>
      </main>
    </template>

    <!-- sign in -->
    <template v-else-if="!admin">
      <SiteHeader minimal />
      <main
        class="section section--top authentication-bg"
        :style="{ backgroundImage: 'url(/images/auth-bg.jpg)' }"
      >
        <div class="container">
          <div class="grid grid--split">
            <section
              v-reveal
              aria-labelledby="admin-title"
            >
              <RouterLink
                to="/"
                class="btn--secondary"
              >
                <AppIcon name="arrow-left" /> Back to home
              </RouterLink>
              <div class="mt-40">
                <span class="sub-title"><strong>Host</strong> console</span>
                <h4
                  id="admin-title"
                  class="fw-6 mt-12"
                >
                  Admin
                </h4>
                <p class="mt-12 muted-text">
                  Sign in to build games, host live sessions, and review results.
                </p>
              </div>
              <form
                class="stack stack--lg mt-30"
                @submit.prevent="signIn"
              >
                <p
                  v-if="error"
                  class="alert"
                  role="alert"
                >
                  {{ error }}
                </p>
                <div class="input-wrapper">
                  <label for="login-email">Email</label>
                  <div class="input-single">
                    <AppIcon name="mail" />
                    <input
                      id="login-email"
                      v-model="email"
                      type="email"
                      autocomplete="username"
                      placeholder="you@example.com"
                      required
                    >
                  </div>
                </div>
                <div class="input-wrapper password-group">
                  <label for="login-password">Password</label>
                  <div class="input-single">
                    <AppIcon name="lock" />
                    <input
                      id="login-password"
                      v-model="password"
                      :type="showPassword ? 'text' : 'password'"
                      autocomplete="current-password"
                      required
                    >
                    <button
                      type="button"
                      :aria-label="showPassword ? 'Hide typed text' : 'Show typed text'"
                      @click="showPassword = !showPassword"
                    >
                      <AppIcon :name="showPassword ? 'eye-off' : 'eye'" />
                    </button>
                  </div>
                </div>
                <div>
                  <button
                    type="submit"
                    class="btn--primary"
                    :disabled="busy"
                  >
                    {{ busy ? 'Signing in…' : 'Sign in' }} <AppIcon name="arrow-right" />
                  </button>
                </div>
              </form>
            </section>
            <div
              v-reveal
              class="text-center"
              aria-hidden="true"
            >
              <img
                src="/images/winner.png"
                alt=""
                style="max-width: min(100%, 26rem); opacity: .9"
              >
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </template>

    <!-- builder -->
    <template v-else>
      <SiteHeader>
        <template #cta>
          <button
            type="button"
            class="btn--ghost"
            :disabled="busy"
            @click="signOut"
          >
            <AppIcon name="logout" /><span class="btn-label">Sign out</span>
          </button>
        </template>
        <template #mobile-cta>
          <button
            type="button"
            class="btn--secondary"
            :disabled="busy"
            @click="signOut"
          >
            Sign out <AppIcon name="logout" />
          </button>
        </template>
      </SiteHeader>
      <main>
        <PageBanner
          title="Game builder"
          crumb="Game builder"
          kicker-strong="Host"
          kicker=" console"
        >
          <span class="tag tag--primary"><AppIcon
            name="user"
            :size="16"
          /> {{ admin.name }}</span>
          <span class="tag"><AppIcon
            name="gamepad"
            :size="16"
          /> {{ activeGames }} active {{ activeGames === 1 ? 'game' : 'games' }}</span>
          <span class="tag"><AppIcon
            name="clock"
            :size="16"
          /> {{ sessions.length }} sessions hosted</span>
        </PageBanner>

        <div class="container section section--tight">
          <p
            v-if="error"
            class="alert mb-24"
            role="alert"
          >
            {{ error }}
          </p>

          <!-- session history -->
          <section
            v-reveal
            class="panel panel--dark"
          >
            <div class="panel__intro">
              <h2 class="text-xxl fw-6">
                Session history
              </h2>
              <button
                type="button"
                class="btn--secondary btn--sm"
                @click="loadSessions"
              >
                <AppIcon
                  name="refresh"
                  :size="18"
                /> Refresh
              </button>
            </div>
            <p
              v-if="!sessions.length"
              class="mt-16 muted-text"
            >
              No sessions have been hosted yet.
            </p>
            <div class="history-grid mt-24">
              <article
                v-for="(session, index) in sessions"
                :key="session.id"
                class="result__single"
              >
                <div class="result__number">
                  #{{ index + 1 }}
                </div>
                <div style="padding-inline-start: 36px">
                  <span class="contest-number"><AppIcon
                    name="hash"
                    :size="16"
                  />{{ session.join_code }}</span>
                </div>
                <h5 class="text-xl fw-6 mt-12">
                  {{ session.game_name }}
                </h5>
                <div class="result__info mt-8">
                  <p class="text-sm">
                    <AppIcon
                      name="hourglass"
                      :size="16"
                    /> {{ easternDate(session.started_at) }} ET
                  </p>
                </div>
                <p class="mt-8">
                  {{ session.status }} · {{ session.player_count }} players · {{ session.team_count }} teams
                </p>
                <p
                  v-if="session.winner"
                  class="secondary-text fw-6 mt-4"
                >
                  Leader: {{ session.winner }}
                </p>
                <div class="button-row mt-16">
                  <RouterLink
                    v-if="session.status === 'ACTIVE'"
                    class="btn--primary btn--sm"
                    :to="`/host/${session.id}`"
                  >
                    Open host
                  </RouterLink>
                  <a
                    class="btn--secondary btn--sm"
                    :href="`/api/admin/session-history/${session.id}/export`"
                    download
                  ><AppIcon
                    name="download"
                    :size="18"
                  />Export CSV</a>
                </div>
              </article>
            </div>
          </section>

          <!-- themes -->
          <section
            v-reveal
            class="panel panel--dark mt-24"
          >
            <div class="panel__intro">
              <h2 class="text-xxl fw-6">
                Game themes
              </h2>
              <button
                type="button"
                class="btn--secondary btn--sm"
                @click="newTheme"
              >
                <AppIcon
                  name="plus"
                  :size="18"
                /> Create theme
              </button>
            </div>
            <div class="button-row mt-16">
              <button
                v-for="theme in themes"
                :key="theme.id"
                type="button"
                class="btn--ghost btn--sm"
                :disabled="theme.system"
                @click="editTheme(theme)"
              >
                <span
                  class="theme-swatch"
                  :style="{ background: theme.configuration.accent }"
                />{{ theme.name }}{{ theme.system ? ' (system)' : '' }}
              </button>
            </div>
            <form
              v-if="themeDraft"
              class="stack stack--lg mt-24 panel"
              @submit.prevent="saveTheme"
            >
              <div class="input-wrapper">
                <label for="theme-name">Theme name</label>
                <div class="input-single input-single--soft">
                  <AppIcon name="palette" />
                  <input
                    id="theme-name"
                    v-model="themeDraft.name"
                    minlength="2"
                    maxlength="80"
                    required
                  >
                </div>
              </div>
              <div class="score-grid">
                <label>Background<input
                  v-model="themeDraft.configuration.background"
                  type="color"
                ></label>
                <label>Primary<input
                  v-model="themeDraft.configuration.primary"
                  type="color"
                ></label>
                <label>Secondary<input
                  v-model="themeDraft.configuration.secondary"
                  type="color"
                ></label>
                <label>Accent<input
                  v-model="themeDraft.configuration.accent"
                  type="color"
                ></label>
                <label>Text<input
                  v-model="themeDraft.configuration.text"
                  type="color"
                ></label>
                <label>Card<input
                  v-model="themeDraft.configuration.cardBackground"
                  type="color"
                ></label>
              </div>
              <div class="input-wrapper">
                <label for="theme-button-style">Button style</label>
                <div class="input-single input-single--soft input-single--select">
                  <select
                    id="theme-button-style"
                    v-model="themeDraft.configuration.buttonStyle"
                  >
                    <option value="rounded">
                      Rounded
                    </option><option value="square">
                      Square
                    </option><option value="pill">
                      Pill
                    </option>
                  </select>
                </div>
              </div>
              <div class="score-grid">
                <label>Logo image<input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  @change="uploadThemeImage($event, 'logoUrl')"
                ></label>
                <label>Background image<input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  @change="uploadThemeImage($event, 'backgroundUrl')"
                ></label>
              </div>
              <div class="button-row">
                <button
                  type="submit"
                  class="btn--primary"
                  :disabled="busy"
                >
                  Save theme
                </button>
                <button
                  type="button"
                  class="btn--ghost"
                  @click="themeDraft = null"
                >
                  Cancel
                </button>
              </div>
            </form>
          </section>

          <div class="admin-grid mt-24">
            <!-- games sidebar -->
            <aside
              v-reveal
              class="panel panel--dark stack stack--lg"
            >
              <h2 class="text-xxl fw-6">
                Games
              </h2>
              <form
                class="stack"
                @submit.prevent="createGame"
              >
                <div class="input-wrapper">
                  <label for="new-game">New game name</label>
                  <div class="input-single">
                    <AppIcon name="gamepad" />
                    <input
                      id="new-game"
                      v-model="newGameName"
                      minlength="2"
                      maxlength="120"
                      placeholder="Friday Night"
                      required
                    >
                  </div>
                </div>
                <button
                  type="submit"
                  class="btn--primary"
                  :disabled="busy"
                >
                  Create game <AppIcon name="arrow-right" />
                </button>
              </form>
              <form
                class="stack panel panel--dashed import-panel"
                @submit.prevent="runImport"
              >
                <h3 class="text-xl fw-6">
                  Import a game file
                </h3>
                <div class="input-wrapper">
                  <label for="import-file">Import file</label>
                  <input
                    id="import-file"
                    ref="importInput"
                    type="file"
                    accept="application/json,.json"
                    @change="chooseImportFile"
                  >
                </div>
                <p
                  v-if="importError"
                  class="alert"
                  role="alert"
                >
                  {{ importError }}
                </p>
                <template v-if="importDocument">
                  <p class="import-summary">
                    {{ importSummary }}
                  </p>
                  <label class="check-row"><input
                    v-model="importMode"
                    type="radio"
                    value="create"
                  > Create a new game</label>
                  <label class="check-row"><input
                    v-model="importMode"
                    type="radio"
                    value="append"
                    :disabled="!selected || selected.archived"
                  > Add rounds to {{ selected && !selected.archived ? selected.name : 'the selected game' }}</label>
                  <button
                    type="submit"
                    class="btn--secondary"
                    :disabled="busy"
                  >
                    <AppIcon name="file-import" /> Import
                  </button>
                </template>
                <p
                  v-if="importWarnings.length"
                  class="notice"
                  role="status"
                >
                  {{ importWarnings.join(' ') }}
                </p>
              </form>
              <article
                v-for="game in games"
                :key="game.id"
                class="list-card"
                :class="{ muted: game.archived }"
              >
                <button
                  class="text-button"
                  type="button"
                  @click="openGame(game.id)"
                >
                  {{ game.name }}
                </button>
                <span
                  v-if="game.archived"
                  class="tag tag--muted"
                >Archived</span>
                <div class="button-row">
                  <a
                    class="btn--ghost btn--sm"
                    :href="exportUrl(game.id)"
                    download
                  ><AppIcon
                    name="download"
                    :size="18"
                  />Export JSON</a>
                  <button
                    type="button"
                    class="btn--ghost btn--sm"
                    @click="duplicateGame(game)"
                  >
                    <AppIcon
                      name="copy"
                      :size="18"
                    /> Duplicate
                  </button>
                  <button
                    v-if="!game.archived"
                    type="button"
                    class="btn--danger btn--sm"
                    @click="archiveGame(game)"
                  >
                    <AppIcon
                      name="archive"
                      :size="18"
                    /> Archive
                  </button>
                </div>
              </article>
            </aside>

            <!-- editor -->
            <section
              v-if="selected"
              class="panel panel--dark stack stack--lg"
            >
              <div>
                <span class="sub-title"><strong>Editing</strong> {{ selected.rounds.length }} {{ selected.rounds.length === 1 ? 'round' : 'rounds' }} · {{ totalQuestions }} {{ totalQuestions === 1 ? 'question' : 'questions' }}</span>
              </div>
              <form
                class="stack"
                @submit.prevent="saveGame"
              >
                <div class="input-wrapper">
                  <label for="edit-game-name">Game name</label>
                  <div class="input-single">
                    <AppIcon name="gamepad" />
                    <input
                      id="edit-game-name"
                      v-model="selected.name"
                      minlength="2"
                      maxlength="120"
                      required
                    >
                  </div>
                </div>
                <div class="input-wrapper">
                  <label for="edit-game-description">Description</label>
                  <div class="input-single input-single--area">
                    <AppIcon name="message" />
                    <textarea
                      id="edit-game-description"
                      v-model="selected.description"
                      maxlength="1000"
                    />
                  </div>
                </div>
                <div class="input-wrapper">
                  <label for="game-theme">Theme</label>
                  <div class="input-single input-single--select">
                    <AppIcon name="palette" />
                    <select
                      id="game-theme"
                      v-model="selected.themeId"
                    >
                      <option
                        v-for="theme in themes"
                        :key="theme.id"
                        :value="theme.id"
                      >
                        {{ theme.name }}
                      </option>
                    </select>
                  </div>
                </div>
                <label class="check-row"><input
                  v-model="selected.settings.profanityFilterEnabled"
                  type="checkbox"
                > Filter profanity on public displays</label>
                <label class="check-row"><input
                  v-model="selected.settings.allowNegativeTotalScore"
                  type="checkbox"
                > Allow negative total scores</label>
                <label class="check-row"><input
                  v-model="selected.settings.answerAveragingEnabled"
                  type="checkbox"
                > Average answers with past plays (small rooms rank against the remembered crowd)</label>
                <div class="input-wrapper">
                  <label for="max-team-size">Maximum team size (optional)</label>
                  <div class="input-single">
                    <AppIcon name="users" />
                    <input
                      id="max-team-size"
                      v-model.number="selected.settings.maxTeamSize"
                      type="number"
                      min="1"
                      max="50"
                    >
                  </div>
                </div>
                <div class="button-row">
                  <button
                    type="submit"
                    class="btn--secondary"
                    :disabled="busy"
                  >
                    Save game settings
                  </button>
                  <button
                    type="button"
                    class="btn--primary"
                    :disabled="busy || !selected.rounds.length"
                    @click="hostGame"
                  >
                    <AppIcon name="play" /> Host this game
                  </button>
                  <a
                    class="btn--ghost"
                    :href="exportUrl(selected.id)"
                    download
                  ><AppIcon name="download" />Export game JSON</a>
                </div>
              </form>

              <hr class="divider divider--dashed">
              <h2 class="text-xxl fw-6">
                Rounds and questions
              </h2>
              <form
                class="inline-form"
                @submit.prevent="addRound"
              >
                <label
                  class="sr-only"
                  for="new-round"
                >Round name</label>
                <div class="input-single">
                  <AppIcon name="flag" />
                  <input
                    id="new-round"
                    v-model="newRoundName"
                    placeholder="Round name"
                    required
                  >
                </div>
                <button
                  type="submit"
                  class="btn--secondary"
                  :disabled="busy"
                >
                  <AppIcon name="plus" /> Add round
                </button>
              </form>
              <div
                v-for="(round, roundIndex) in selected.rounds"
                :key="round.id"
                class="round-card"
              >
                <div class="editor-heading">
                  <div class="input-single input-single--soft">
                    <span class="avatar avatar--secondary">{{ roundIndex + 1 }}</span>
                    <input
                      v-model="round.name"
                      :aria-label="`Round ${roundIndex + 1} name`"
                    >
                  </div>
                  <div class="compact-actions">
                    <button
                      type="button"
                      class="btn--ghost btn--sm btn--icon"
                      :disabled="roundIndex === 0"
                      :aria-label="`Move ${round.name} up`"
                      @click="moveRound(roundIndex, -1)"
                    >
                      <AppIcon name="chevron-up" />
                    </button>
                    <button
                      type="button"
                      class="btn--ghost btn--sm btn--icon"
                      :disabled="roundIndex === selected.rounds.length - 1"
                      :aria-label="`Move ${round.name} down`"
                      @click="moveRound(roundIndex, 1)"
                    >
                      <AppIcon name="chevron-down" />
                    </button>
                    <button
                      type="button"
                      class="btn--secondary btn--sm"
                      @click="saveRound(round)"
                    >
                      Save
                    </button>
                    <a
                      class="btn--ghost btn--sm"
                      :href="exportUrl(selected.id, round.id)"
                      :aria-label="`Export ${round.name}`"
                      download
                    ><AppIcon
                      name="download"
                      :size="18"
                    />Export</a>
                    <button
                      type="button"
                      class="btn--danger btn--sm"
                      @click="deleteRound(round)"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <p
                  v-if="!round.questions.length"
                  class="muted-text"
                >
                  No questions in this round yet.
                </p>
                <ol class="question-list">
                  <li
                    v-for="(question, questionIndex) in round.questions"
                    :key="question.id"
                    class="question-editor"
                  >
                    <details>
                      <summary>
                        <span class="row-flex"><span class="tag tag--primary">{{ typeLabels[kind(question)] }}</span>{{ question.prompt }}</span>
                        <strong>{{ question.points }} pts</strong>
                      </summary>
                      <div class="stack">
                        <label>Prompt<div class="input-single input-single--soft input-single--area"><textarea
                          v-model="question.prompt"
                          maxlength="500"
                        /></div></label>
                        <label>Question image (optional)<input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          @change="uploadQuestionImage(question, $event)"
                        ></label>
                        <img
                          v-if="question.imageUrl"
                          class="builder-image-preview"
                          :src="question.imageUrl"
                          alt="Question image preview"
                        >
                        <button
                          v-if="question.imageUrl"
                          type="button"
                          class="btn--danger btn--sm"
                          @click="question.imageUrl = null"
                        >
                          Remove question image
                        </button>
                        <label>Round<div class="input-single input-single--soft input-single--select"><select v-model="question.roundId">
                          <option
                            v-for="destination in selected.rounds"
                            :key="destination.id"
                            :value="destination.id"
                          >{{ destination.name }}</option>
                        </select></div></label>
                        <div class="score-grid">
                          <label>Points<div class="input-single input-single--soft"><input
                            v-model.number="question.points"
                            type="number"
                            min="0"
                            max="10000"
                          ></div></label>
                          <label>Timer seconds<div class="input-single input-single--soft"><input
                            v-model.number="question.timeLimitSeconds"
                            type="number"
                            min="5"
                            max="600"
                          ></div></label>
                        </div>
                        <label>Host notes<div class="input-single input-single--soft input-single--area"><textarea
                          v-model="question.hostNotes"
                          maxlength="2000"
                        /></div></label>
                        <label v-if="!kind(question).startsWith('NUMERIC')">Standard answers (one per line, `answer = count`{{ kind(question).startsWith('MULTIPLE_CHOICE') ? ', using choice ids' : '' }})<div class="input-single input-single--soft input-single--area"><textarea
                          v-model="standardAnswerText[question.id]"
                          rows="5"
                          placeholder="Toothbrush = 12"
                        /></div></label>
                        <div
                          v-if="kind(question) === 'NUMERIC_MEDIAN'"
                          class="score-grid"
                        >
                          <label>Closest<div class="input-single input-single--soft"><input
                            v-model.number="question.scoring.closest"
                            type="number"
                          ></div></label>
                          <label>Second closest<div class="input-single input-single--soft"><input
                            v-model.number="question.scoring.secondClosest"
                            type="number"
                          ></div></label>
                          <label>Remaining<div class="input-single input-single--soft"><input
                            v-model.number="question.scoring.remaining"
                            type="number"
                          ></div></label>
                        </div>
                        <div
                          v-else-if="kind(question) === 'NUMERIC_MODE'"
                          class="score-grid"
                        >
                          <label>Winning mode<div class="input-single input-single--soft"><input
                            v-model.number="question.scoring.winning"
                            type="number"
                          ></div></label>
                          <label>Other<div class="input-single input-single--soft"><input
                            v-model.number="question.scoring.other"
                            type="number"
                          ></div></label>
                        </div>
                        <div
                          v-else
                          class="score-grid"
                        >
                          <label>First<div class="input-single input-single--soft"><input
                            v-model.number="question.scoring.rank1"
                            type="number"
                          ></div></label>
                          <label>Second<div class="input-single input-single--soft"><input
                            v-model.number="question.scoring.rank2"
                            type="number"
                          ></div></label>
                          <label>Third<div class="input-single input-single--soft"><input
                            v-model.number="question.scoring.rank3"
                            type="number"
                          ></div></label>
                          <label>Other<div class="input-single input-single--soft"><input
                            v-model.number="question.scoring.other"
                            type="number"
                          ></div></label>
                        </div>
                        <label class="check-row"><input
                          v-model="question.active"
                          type="checkbox"
                        > Active</label>
                        <div class="button-row">
                          <button
                            type="button"
                            class="btn--ghost btn--sm"
                            :disabled="questionIndex === 0"
                            @click="moveQuestion(round, questionIndex, -1)"
                          >
                            Move up
                          </button>
                          <button
                            type="button"
                            class="btn--ghost btn--sm"
                            :disabled="questionIndex === round.questions.length - 1"
                            @click="moveQuestion(round, questionIndex, 1)"
                          >
                            Move down
                          </button>
                          <button
                            type="button"
                            class="btn--secondary btn--sm"
                            @click="saveQuestion(question)"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            class="btn--ghost btn--sm"
                            @click="duplicateQuestion(question)"
                          >
                            Duplicate
                          </button>
                          <button
                            type="button"
                            class="btn--danger btn--sm"
                            @click="deleteQuestion(question)"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </details>
                  </li>
                </ol>
              </div>

              <form
                v-if="selected.rounds.length"
                class="stack panel"
                @submit.prevent="addQuestion"
              >
                <h3 class="text-xl fw-6">
                  Add question
                </h3>
                <div class="score-grid">
                  <div class="input-wrapper">
                    <label for="question-type">Question type</label>
                    <div class="input-single input-single--soft input-single--select">
                      <select
                        id="question-type"
                        v-model="questionType"
                      >
                        <option
                          v-for="(label, type) in typeLabels"
                          :key="type"
                          :value="type"
                        >
                          {{ label }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="input-wrapper">
                    <label for="question-round">Round</label>
                    <div class="input-single input-single--soft input-single--select">
                      <select
                        id="question-round"
                        v-model="questionRoundId"
                      >
                        <option
                          v-for="round in selected.rounds"
                          :key="round.id"
                          :value="round.id"
                        >
                          {{ round.name }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="input-wrapper">
                  <label for="new-question">Prompt</label>
                  <div class="input-single input-single--soft input-single--area">
                    <AppIcon name="message" />
                    <textarea
                      id="new-question"
                      v-model="newPrompt"
                      maxlength="500"
                      placeholder="Name something people always forget to pack."
                      required
                    />
                  </div>
                </div>
                <div class="input-wrapper">
                  <label for="question-image">Question image (optional)</label>
                  <input
                    id="question-image"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    @change="uploadNewQuestionImage"
                  >
                </div>
                <img
                  v-if="questionImageUrl"
                  class="builder-image-preview"
                  :src="questionImageUrl"
                  alt="Question image preview"
                >
                <template v-if="questionType.startsWith('MULTIPLE_CHOICE')">
                  <div class="input-wrapper">
                    <label for="question-choices">{{ questionType === 'MULTIPLE_CHOICE_IMAGE' ? 'Image URLs' : 'Choices' }} (one per line)</label>
                    <div class="input-single input-single--soft input-single--area">
                      <textarea
                        id="question-choices"
                        v-model="choiceLines"
                        rows="6"
                        required
                      />
                    </div>
                  </div>
                  <p class="muted-text">
                    Provide 2–6 {{ questionType === 'MULTIPLE_CHOICE_IMAGE' ? 'uploaded or HTTPS images' : 'choices' }}.
                  </p>
                  <label v-if="questionType === 'MULTIPLE_CHOICE_IMAGE'">Upload choice images<input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    multiple
                    @change="uploadChoiceImages"
                  ></label>
                </template>
                <div
                  v-if="!questionType.startsWith('NUMERIC')"
                  class="input-wrapper"
                >
                  <label for="question-standard-answers">Standard answers (optional, one per line, `answer = count`{{ questionType.startsWith('MULTIPLE_CHOICE') ? ', using choice ids' : '' }})</label>
                  <div class="input-single input-single--soft input-single--area">
                    <AppIcon name="users" />
                    <textarea
                      id="question-standard-answers"
                      v-model="newStandardAnswers"
                      rows="4"
                      placeholder="Toothbrush = 12&#10;Sunscreen = 9"
                    />
                  </div>
                </div>
                <div class="score-grid">
                  <div class="input-wrapper">
                    <label for="question-points">Base points</label>
                    <div class="input-single input-single--soft">
                      <AppIcon name="medal" />
                      <input
                        id="question-points"
                        v-model.number="questionPoints"
                        type="number"
                        min="0"
                        max="10000"
                        required
                      >
                    </div>
                  </div>
                  <div class="input-wrapper">
                    <label for="question-timer">Timer seconds (optional)</label>
                    <div class="input-single input-single--soft">
                      <AppIcon name="clock" />
                      <input
                        id="question-timer"
                        v-model.number="questionTimeLimit"
                        type="number"
                        min="5"
                        max="600"
                      >
                    </div>
                  </div>
                </div>
                <fieldset
                  v-if="questionType === 'NUMERIC_MEDIAN'"
                  class="panel panel--soft"
                >
                  <legend class="fw-6">
                    Scoring by distance
                  </legend>
                  <div class="score-grid">
                    <label>Closest<div class="input-single input-single--soft"><input
                      v-model.number="closest"
                      type="number"
                    ></div></label>
                    <label>Second closest<div class="input-single input-single--soft"><input
                      v-model.number="secondClosest"
                      type="number"
                    ></div></label>
                    <label>Remaining<div class="input-single input-single--soft"><input
                      v-model.number="remaining"
                      type="number"
                    ></div></label>
                  </div>
                </fieldset>
                <fieldset
                  v-else-if="questionType === 'NUMERIC_MODE'"
                  class="panel panel--soft"
                >
                  <legend class="fw-6">
                    Mode scoring
                  </legend>
                  <div class="score-grid">
                    <label>Winning mode<div class="input-single input-single--soft"><input
                      v-model.number="modeWinning"
                      type="number"
                    ></div></label>
                    <label>Other<div class="input-single input-single--soft"><input
                      v-model.number="other"
                      type="number"
                    ></div></label>
                  </div>
                </fieldset>
                <fieldset
                  v-else
                  class="panel panel--soft"
                >
                  <legend class="fw-6">
                    Scoring by crowd rank
                  </legend>
                  <div class="score-grid">
                    <label>First<div class="input-single input-single--soft"><input
                      v-model.number="rank1"
                      type="number"
                    ></div></label>
                    <label>Second<div class="input-single input-single--soft"><input
                      v-model.number="rank2"
                      type="number"
                    ></div></label>
                    <label>Third<div class="input-single input-single--soft"><input
                      v-model.number="rank3"
                      type="number"
                    ></div></label>
                    <label>Other<div class="input-single input-single--soft"><input
                      v-model.number="other"
                      type="number"
                    ></div></label>
                  </div>
                </fieldset>
                <div>
                  <button
                    type="submit"
                    class="btn--primary"
                    :disabled="busy"
                  >
                    <AppIcon name="plus" /> Add question
                  </button>
                </div>
              </form>
            </section>
            <section
              v-else
              class="panel panel--dark text-center"
            >
              <img
                src="/images/trophy.png"
                alt=""
                style="max-width: 10rem; opacity: .6"
              >
              <h2 class="text-xxl fw-6 mt-16">
                Select or create a game
              </h2>
              <p class="muted-text mt-8">
                Pick a game from the list, create a new one, or import a game file to get started.
              </p>
            </section>
          </div>
        </div>
      </main>
      <SiteFooter />
    </template>
  </div>
</template>
